import React, { createContext, useContext, useState, useEffect } from 'react';
import { getUserFromToken, requestMagicLink as sendMagicLink, logout } from '../services/authService';
import { User } from '../types/User';

const API_BASE_URL = import.meta.env.VITE_FRONTEND_URL;

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  requestMagicLink: (email: string, turnstileToken: string) => Promise<void>;
  verifyMagicLink: (token: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initUser = async () => {
      try {
        const user = await getUserFromToken();
        setCurrentUser(user);
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initUser();
  }, []);

  const requestMagicLink = async (email: string, turnstileToken: string) => {
    await sendMagicLink(email, turnstileToken);
  };

  const verifyMagicLink = async (token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Invalid or expired link');
      }

      const { user, token: authToken } = await response.json();
      localStorage.setItem('authToken', authToken);
      setCurrentUser(user);
    } catch (error) {
      console.error('Verify magic link error:', error);
      throw error;
    }
  };

  const handleLogout = () => {
    logout();
    setCurrentUser(null);
  };

  const value = {
    currentUser,
    loading,
    requestMagicLink,
    verifyMagicLink,
    logout: handleLogout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};